import { Typography } from '@mui/material'
import {
  ErrorBoundary,
  browserTracingIntegration,
  replayIntegration,
  init as sentryInit,
  setUser,
} from '@sentry/react'
import { FC, PropsWithChildren, useEffect } from 'react'

import p from '../package.json'

import useUser from './hooks/useUser'

sentryInit({
  dsn: 'https://3b09f71dc3ba7d0deafe365009527f4c@o4506705917378560.ingest.sentry.io/4506705921966080',
  enabled: !/\.local$/.test(location.hostname),
  environment: STAGE,
  integrations: [
    browserTracingIntegration({
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [/^https:\/\/(.*)-api\.aristotle\.ai/],
    }),
    // feedbackIntegration({ colorScheme: 'light' }),
    replayIntegration({ maskAllText: false, blockAllMedia: false }),
  ],
  release: p.version,
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions

  /**
   * Session Replay
   *
   * replaysSessionsSampleRate
   * This sets the sample rate at 10%. You may want to change it to 100% while in development and then
   * sample at a lower rate in production.
   *
   * replaysOnErrorSampleRate
   * If you're not already sampling the entire session, change the sample rate to 100% when sampling
   * sessions where errors occur.
   */
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
})

// In dev/local, surface the real error + component stack so migration
// integration issues are diagnosable on-screen (Sentry still reports).
const isDev = /\.local$/.test(location.hostname) || /localhost/.test(location.hostname)

const fallback = (errorData: { error: unknown; componentStack?: string }) => {
  if (!isDev) {
    return (
      <Typography variant="body1">
        Oh no! A serious error has occurred. Please contact an administrator.
      </Typography>
    )
  }
  const err = errorData?.error as Error
  return (
    <pre
      style={{
        whiteSpace: 'pre-wrap',
        padding: 16,
        margin: 0,
        fontFamily: 'Menlo, Consolas, monospace',
        fontSize: 12,
        color: '#b22743',
        background: '#fff5f7',
        overflow: 'auto',
      }}
    >
      {`Render error: ${err?.message ?? String(errorData?.error)}\n\n${err?.stack ?? ''}\n\nComponent stack:${errorData?.componentStack ?? ''}`}
    </pre>
  )
}

interface ErrorTracingProps {}

const ErrorTracing: FC<PropsWithChildren<ErrorTracingProps>> = ({ children }) => {
  const { user } = useUser()

  useEffect(() => {
    user?.id && setUser({ id: user.id })
  }, [user?.id])

  return (
    <ErrorBoundary fallback={fallback} showDialog>
      {children}
    </ErrorBoundary>
  )
}

export default ErrorTracing
